import type { RefObject } from "react";

import { HERO_PROGRESS, HERO_TRIGGER, isThinLink } from "@/components/hero";
import { EASE_OUT, ScrollSmoother, ScrollTrigger, SplitText, gsap, useGSAP } from "@/lib/gsap";

// فقط داخل افکت‌ها صدا زده می‌شود، پس همیشه در مرورگر هستیم. خط کند هم مثل کاهش حرکت است: هیرو فقط
// فریم آخر را می‌گیرد (hero.tsx)، پس پین و اسکراب و ریویل‌ها هم نمی‌آیند و متن هیرو از اول دیده می‌شود
const reduceMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches || isThinLink();

/**
 * حرکتِ صفحه‌ی اصلی، یک‌جا:
 * - اسکرول نرم (ScrollSmoother) روی دسکتاپ؛ روی لمسی اسکرول بومی.
 * - هیرو پین می‌شود و اسکرول پرده را از روی خودرو کنار می‌زند (بوم و فریم‌ها در hero.tsx).
 * - هدر بالای هیرو شفاف است و بعد از آن به حالت عادی برمی‌گردد.
 * - لینک‌های #hash با اسکرول نرم به مقصد می‌رسند.
 * ریویل‌ها (data-reveal / data-split / data-clip / data-fill) در اثر دوم می‌آیند که با رسیدن داده دوباره ساخته می‌شود.
 */
export function useHomeMotion(root: RefObject<HTMLElement | null>, revealDeps: unknown[]) {
  useGSAP(
    () => {
      const scope = root.current;
      if (!scope) return;
      const header = scope.querySelector<HTMLElement>(".home-header");
      const hero = scope.querySelector<HTMLElement>(".hero");
      const wrapper = scope.querySelector<HTMLElement>("#smooth-wrapper");
      const content = scope.querySelector<HTMLElement>("#smooth-content");
      if (!hero || !wrapper || !content) return;

      const reduce = reduceMotion();
      // اسکرول نرم فقط برای ماوس و ترک‌پد؛ روی لمسی اسکرول بومی (با smoothTouch: false هم لایه‌ی
      // fixed و ترنسفورمش می‌ماند و پین را با نوار نشانی و مومنتومِ iOS ناهماهنگ می‌کرد)
      const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      const smoother =
        reduce || !fine
          ? null
          : ScrollSmoother.create({
              wrapper,
              content,
              smooth: 1.1,
              effects: false,
              smoothTouch: false,
              normalizeScroll: false,
            });

      // scrollIntoView (از روتر یا مرورگر) پوششِ overflow:hidden را داخلی اسکرول می‌کند و
      // نقشه‌ی ScrollTrigger به هم می‌ریزد؛ هر اسکرول داخلی همان لحظه صفر می‌شود
      // (هم افقی: تمرکز روی پیوند پاورقی که نیمه‌اش بیرون از عرض صفحه است، پوشش را ۲۵۶ پیکسل
      // به کنار می‌برد و نوار سفیدی در یک سمت می‌ماند)
      const guard = () => {
        if (wrapper.scrollTop !== 0) wrapper.scrollTop = 0;
        if (wrapper.scrollLeft !== 0) wrapper.scrollLeft = 0;
      };
      wrapper.addEventListener("scroll", guard);

      // لینک‌های داخل صفحه (/#consult و #consult) با اسکرول نرم می‌روند
      const onClick = (event: MouseEvent) => {
        // SAFETY: click targets inside the page root are DOM elements (or null for detached nodes)
        const anchor = (event.target as HTMLElement | null)?.closest<HTMLAnchorElement>(
          'a[href^="#"], a[href^="/#"]',
        );
        if (!anchor || event.defaultPrevented) return;
        const id = anchor.getAttribute("href")?.replace(/^\/?#/, "");
        const target = id ? document.getElementById(id) : null;
        if (!target) return;
        event.preventDefault();
        history.replaceState(history.state, "", `#${id}`);
        if (smoother) smoother.scrollTo(target, true, "top 96px");
        else target.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      scope.addEventListener("click", onClick);
      if (location.hash) {
        const target = document.getElementById(location.hash.slice(1));
        if (target) gsap.delayedCall(0.4, () => smoother?.scrollTo(target, false, "top 96px"));
      }

      const mm = gsap.matchMedia();
      mm.add(
        {
          desktop: "(min-width: 1024px)",
          portrait: "(orientation: portrait)",
          motion: "(prefers-reduced-motion: no-preference)",
        },
        (context) => {
          const { desktop, portrait, motion } = context.conditions ?? {};
          const setScrolled = (on: boolean) => header?.classList.toggle("is-scrolled", on);

          if (motion && !reduce) {
            // پرده‌برداری: هیرو پین می‌شود و پیشرفت اسکرول به بوم هیرو می‌رود (فریم را خودش نرم می‌کند)؛
            // متن اول بالا می‌رود و تیتر پایانی روی خودروی کامل می‌نشیند. هدر تا آمدن بخش بعدی شفاف است.
            const next = hero.nextElementSibling;
            const timeline = gsap
              .timeline({
                scrollTrigger: {
                  id: HERO_TRIGGER,
                  trigger: hero,
                  start: "top top",
                  end: desktop ? "+=260%" : "+=200%",
                  pin: true,
                  scrub: true,
                  anticipatePin: 1,
                  onUpdate: () => hero.dispatchEvent(new Event(HERO_PROGRESS)),
                },
                defaults: { ease: "none" },
              })
              .to(".hero-copy", { yPercent: -16, autoAlpha: 0, duration: 0.14 }, 0.02)
              .fromTo(
                ".hero-reveal",
                { autoAlpha: 0, y: 48 },
                { autoAlpha: 1, y: 0, duration: 0.12, ease: "power2.out" },
                0.78,
              )
              .to({}, { duration: 0.1 }, 0.9);
            // صفحه‌ی ایستاده: خودرو در پایان وسط صحنه است و پرده‌ی تیره‌ی بالا رویش می‌افتاد؛ همراه متن اول
            // می‌رود. صفحه‌ی خوابیده (دوربین کاور) پرده را نگه می‌دارد چون تیتر پایانی روی خودرو می‌نشیند
            if (portrait) timeline.to(".hero-scrim", { autoAlpha: 0, duration: 0.14 }, 0.02);
            // پایان پین هنوز صحنه تمام‌صفحه است؛ هدر وقتی مات می‌شود که بخش بعدی تا نیمه‌ی صفحه بالا آمده
            // (زودتر روی صحنه‌ی تیره زمخت است، دیرتر متن یخیِ هدر روی بدنه‌ی سفید خودرو گم می‌شود)
            if (next) {
              ScrollTrigger.create({
                trigger: next,
                start: "top 55%",
                onEnter: () => setScrolled(true),
                onLeaveBack: () => setScrolled(false),
              });
              // هنگام رفتن هیرو، تیتر پایانی پیش از رسیدن به هدرِ شفاف محو می‌شود؛ روی فرزندها، چون
              // خودِ .hero-reveal را تایم‌لاین پین می‌گرداند
              gsap.fromTo(
                ".hero-reveal > *",
                { autoAlpha: 1, y: 0 },
                {
                  autoAlpha: 0,
                  y: -32,
                  ease: "none",
                  immediateRender: false,
                  scrollTrigger: {
                    trigger: next,
                    start: "top bottom",
                    end: "top 45%",
                    scrub: true,
                  },
                },
              );
            }
            // با عوض شدن شرط (چرخش صفحه، تغییر «کاهش حرکت») شاخه‌ی بعدی از هدر شفاف شروع می‌کند
            return () => setScrolled(false);
          }

          ScrollTrigger.create({
            trigger: hero,
            start: "top+=55% top",
            onEnter: () => setScrolled(true),
            onLeaveBack: () => setScrolled(false),
          });
          return () => setScrolled(false);
        },
      );

      return () => {
        scope.removeEventListener("click", onClick);
        wrapper.removeEventListener("scroll", guard);
        mm.revert();
        smoother?.kill();
      };
    },
    { scope: root },
  );

  useGSAP(
    () => {
      const scope = root.current;
      if (!scope || reduceMotion()) return;
      const enter = (trigger: Element) => ({ trigger, start: "top 85%", once: true });

      gsap.utils.toArray<HTMLElement>("[data-reveal]", scope).forEach((el) => {
        const delay = Number(el.dataset.reveal || 0);
        gsap.from(el, {
          autoAlpha: 0,
          y: 28,
          duration: 1.1,
          delay,
          ease: EASE_OUT,
          scrollTrigger: enter(el),
        });
      });

      gsap.utils.toArray<HTMLElement>("[data-reveal-group]", scope).forEach((group) => {
        gsap.from(Array.from(group.children), {
          autoAlpha: 0,
          y: 24,
          duration: 1,
          stagger: 0.07,
          ease: EASE_OUT,
          scrollTrigger: enter(group),
        });
      });

      gsap.utils.toArray<HTMLElement>("[data-clip]", scope).forEach((el) => {
        gsap.fromTo(
          el,
          { clipPath: "inset(0 0 100% 0 round 1rem)" },
          {
            clipPath: "inset(0 0 0% 0 round 1rem)",
            duration: 1.3,
            ease: "expo.inOut",
            scrollTrigger: enter(el),
          },
        );
      });

      const splits: SplitText[] = [];
      document.fonts.ready.then(() => {
        gsap.utils.toArray<HTMLElement>("[data-split]", scope).forEach((el) => {
          splits.push(
            SplitText.create(el, {
              type: "lines",
              mask: "lines",
              linesClass: "split-line",
              autoSplit: true,
              onSplit: (self) =>
                gsap.from(self.lines, {
                  yPercent: 112,
                  duration: 1.15,
                  stagger: 0.09,
                  ease: EASE_OUT,
                  scrollTrigger: enter(el),
                }),
            }),
          );
        });
        ScrollTrigger.refresh();
      });

      // نشان بزرگ پابرگ: از خطِ دورگیری به توپُر، هم‌قدم با اسکرول و از راست به چپ
      gsap.utils.toArray<HTMLElement>("[data-fill]", scope).forEach((el) => {
        // برشِ منفی بالا و پایین تا دنباله‌ی حروف (و، ر، ن) هم پُر شود
        gsap.fromTo(
          el,
          { clipPath: "inset(-0.25em 0 -0.4em 100%)" },
          {
            clipPath: "inset(-0.25em 0 -0.4em 0%)",
            ease: "none",
            scrollTrigger: {
              trigger: el.parentElement ?? el,
              start: "top 90%",
              end: "bottom 100%",
              scrub: true,
            },
          },
        );
      });

      return () => splits.forEach((split) => split.revert());
    },
    { scope: root, dependencies: revealDeps, revertOnUpdate: true },
  );
}
